import React, { useCallback, useEffect, useState } from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  select: {
    minWidth: 240,
    maxWidth: 420,
  },
  cell: {
    verticalAlign: "top",
  },
}));

const ConnectionCtaButtonAssign = () => {
  const classes = useStyles();
  const [whatsApps, setWhatsApps] = useState([]);
  const [buttons, setButtons] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [assignRes, buttonsRes] = await Promise.all([
        api.get("/cta-url-buttons/assignments"),
        api.get("/cta-url-buttons"),
      ]);
      setWhatsApps(Array.isArray(assignRes.data) ? assignRes.data : []);
      setButtons(Array.isArray(buttonsRes.data) ? buttonsRes.data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener("cta-buttons-changed", onChanged);
    return () => window.removeEventListener("cta-buttons-changed", onChanged);
  }, [load]);

  const handleAssign = async (whatsAppId, buttonIds) => {
    setSavingId(whatsAppId);
    try {
      const { data } = await api.put(
        `/whatsapp/${whatsAppId}/cta-url-buttons`,
        { buttonIds }
      );
      setWhatsApps(prev =>
        prev.map(w =>
          w.id === whatsAppId
            ? {
                ...w,
                buttonIds: data.buttonIds || [],
                ctaUrlButtons: data.ctaUrlButtons || [],
              }
            : w
        )
      );
      toast.success(i18n.t("settings.settings.ctaButtons.assignSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingId(null);
    }
  };

  const labelFor = button =>
    button.name || button.displayText || `#${button.id}`;

  return (
    <Paper className={classes.paper} variant="outlined">
      <Typography variant="h6">
        {i18n.t("settings.settings.ctaButtons.assignTitle")}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {i18n.t("settings.settings.ctaButtons.assignHelp")}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              {i18n.t("settings.settings.aiPromptAssign.connection")}
            </TableCell>
            <TableCell>
              {i18n.t("settings.settings.ctaButtons.linkedButtons")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {whatsApps.map(wa => {
            const selected = wa.buttonIds || [];
            return (
              <TableRow key={wa.id}>
                <TableCell className={classes.cell}>
                  <Typography variant="body2">{wa.name}</Typography>
                  {wa.displayPhoneNumber ? (
                    <Typography variant="caption" color="textSecondary">
                      {wa.displayPhoneNumber}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell className={classes.cell}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.select}
                    disabled={savingId === wa.id}
                  >
                    <InputLabel shrink>
                      {i18n.t("settings.settings.ctaButtons.linkedButtons")}
                    </InputLabel>
                    <Select
                      multiple
                      displayEmpty
                      label={i18n.t(
                        "settings.settings.ctaButtons.linkedButtons"
                      )}
                      value={selected}
                      onChange={e =>
                        handleAssign(wa.id, e.target.value || [])
                      }
                      renderValue={values => {
                        if (!values.length) {
                          return i18n.t(
                            "settings.settings.ctaButtons.useAllButtons"
                          );
                        }
                        return values
                          .map(id => {
                            const button = buttons.find(b => b.id === id);
                            return button ? labelFor(button) : `#${id}`;
                          })
                          .join(", ");
                      }}
                    >
                      {buttons.map(button => (
                        <MenuItem key={button.id} value={button.id}>
                          <Checkbox
                            checked={selected.indexOf(button.id) > -1}
                            color="primary"
                          />
                          <ListItemText primary={labelFor(button)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            );
          })}
          {!whatsApps.length ? (
            <TableRow>
              <TableCell colSpan={2}>
                {i18n.t("settings.settings.aiPromptAssign.empty")}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default ConnectionCtaButtonAssign;
